import { Gericht, useAddGerichtMutation, useListGerichteQuery } from "../gerichte/gerichteConnector";
import { useAddListenGerichtMutation } from "./listenConnector";
import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Box } from "@mui/system";

type GerichtOptionValue = {
    type: "gericht";
    menge: number;
    gericht: Gericht;
    zuletzt?: string;
} |
{
    type: "neuesGericht";
    menge: number;
    name: string;
};
function parseGerichtInput(input: string): { name: string, menge: number } {
    const mengenRegex1 = /^(?<menge>\d+)\s?x?\s(?<name>.*)$/i;
    const mengenRegex2 = /^(?<name>.*)\sx?\s?(?<menge>\d+)$/i;

    const inputTrimmed = input.trim();

    const match = mengenRegex1.exec(inputTrimmed)?.groups
        ?? mengenRegex2.exec(inputTrimmed)?.groups;
    if (match) {
        return {
            name: match.name,
            menge: parseInt(match.menge)
        }
    }
    else {
        return {
            name: inputTrimmed,
            menge: 1
        }
    }
}
export function GerichtHinzufuegenComponent(props: { listeId: number; }) {
    const { data: gerichte } = useListGerichteQuery();
    const [text, setText] = React.useState("");
    const [addListenGericht] = useAddListenGerichtMutation();
    const [addGericht] = useAddGerichtMutation();

    // It's a little complicated to integrate this in the RTK Query connector,
    // since it's invalidated on tags it doesn't provide. It's probably easiest
    // to just fetch it right here.
    const [vorschlaege, setVorschlaege] = React.useState<{ id: number, name: string, zuletzt: string }[]>([]);
    React.useEffect(() => {
        async function fetchData() {
            const response = await fetch(`/api/listen/${props.listeId}/vorschlaege`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`
                }
            });
            const data = await response.json();
            setVorschlaege(data);
        }
        fetchData();
    }, [gerichte, props.listeId, setVorschlaege]);

    if (!gerichte)
        return <>Loading...</>;

    return <Autocomplete
        autoHighlight
        inputValue={text}
        onInputChange={(e, newValue, reason) => reason === "reset" ? setText("") : setText(newValue ?? "")}
        filterOptions={(options, params) => {
            const { name, menge } = parseGerichtInput(params.inputValue);
            if (!name) {
                return vorschlaege.map(({ id, name, zuletzt }) => ({ type: "gericht", gericht: { id, name }, menge: 1, zuletzt } as GerichtOptionValue));
            }

            const searchString = name.toLowerCase();
            console.log("Filtering: ", searchString);
            const filtered = options
                .filter(x => x.type === "gericht" && x.gericht.name.toLowerCase().includes(searchString))
                .map(x => ({ ...x, menge }));
            filtered.push({ type: "neuesGericht", name, menge });
            return filtered;
        }}
        renderOption={(props, option) => <Box component="li" {...props}>
            {option.menge !== 1 && <span style={{ opacity: 0.5 }}>{option.menge}x&ensp;</span>}
            {option.type === "neuesGericht" ?
                <i>Neues Gericht: {option.name}</i> :
                <>{option.gericht.name}</>
            }
            {option.type === "gericht" && option.zuletzt && <span className="zuletzt-gehabt">(zuletzt gehabt: <ZuletztGehabt date={option.zuletzt} />)</span>}
        </Box>}
        renderInput={params => <TextField
            {...params}
            label="Gericht hinzufügen" />}
        value={null}
        onChange={async (e, newValue) => {
            if (!newValue)
                return;
            if (newValue.type === "gericht")
                addListenGericht({ listeId: props.listeId, gerichtId: newValue.gericht.id, menge: newValue.menge });
            else {
                if (!window.confirm(`Sicher, dass das folgende Gericht angelegt werden soll?\n\n${newValue.name}`))
                    return;

                const result = await addGericht(newValue.name);
                if ("error" in result) {
                    window.alert(`Fehler beim Hinzufügen des Gerichtes: ${JSON.stringify(result.error)}`);
                    return;
                }
                addListenGericht({ listeId: props.listeId, gerichtId: result.data.id, menge: newValue.menge });
            }
            setText("");
        }}
        options={gerichte.map(gericht => ({ type: "gericht", gericht, menge: 1 } as GerichtOptionValue))}
        getOptionLabel={option => option.type === "neuesGericht" ? "neuesGericht" : option.gericht.id.toString()}
    />;
}

function ZuletztGehabt(props: { date: string }) {
    const date = new Date(props.date);
    const timeDelta = Date.now() - date.getTime();
    const week = 7 * 24 * 60 * 60 * 1000;
    if (timeDelta < 0)
        return <>diese Woche</>
    if (timeDelta < 0.5 * week)
        return <>letzte Woche</>
    if (timeDelta < 1.5 * week)
        return <>vorletzte Woche</>

    const weeks = Math.floor(timeDelta / week - 0.5);
    return <>vor {weeks} Wochen</>
}
